import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  getTasks, 
  getLabSamples, 
  getReferralCases, 
  getReviewRequests,
  getDashboardSummary,
  getReferringClinics,
  getStaff
} from './api';

const DataContext = createContext(null);

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export const DataProvider = ({ children }) => {
  const [cache, setCache] = useState({
    tasks: { data: [], timestamp: 0 },
    labSamples: { data: [], timestamp: 0 },
    referralCases: { data: [], timestamp: 0 },
    referringClinics: { data: [], timestamp: 0 },
    reviewRequests: { data: [], timestamp: 0 },
    staff: { data: [], timestamp: 0 },
    dashboardSummary: { data: null, timestamp: 0 }
  });
  
  const [loading, setLoading] = useState({
    tasks: false,
    labSamples: false,
    referralCases: false,
    referringClinics: false,
    reviewRequests: false,
    staff: false,
    dashboardSummary: false
  });

  // Use refs to avoid stale closures in fetchData without causing infinite re-renders
  const cacheRef = useRef(cache);
  const loadingRef = useRef(loading);
  cacheRef.current = cache;
  loadingRef.current = loading;

  // Generic fetch function with caching (stable reference via refs)
  const fetchData = useCallback(async (key, fetchFn, force = false) => {
    const currentCache = cacheRef.current;
    const currentLoading = loadingRef.current;

    // Return cached data immediately if valid and not forcing refresh
    if (!force && currentCache[key].timestamp > 0 && (Date.now() - currentCache[key].timestamp) < CACHE_DURATION) {
      return currentCache[key].data;
    }

    // If already loading, return current cache
    if (currentLoading[key]) {
      return currentCache[key].data;
    }

    setLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await fetchFn();
      const newData = response.data || [];
      
      setCache(prev => ({
        ...prev,
        [key]: { data: newData, timestamp: Date.now() }
      }));
      
      return newData;
    } catch (error) {
      console.error(`Error fetching ${key}:`, error);
      return currentCache[key].data;
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // Specific fetch functions
  const fetchTasks = useCallback((force = false) => fetchData('tasks', getTasks, force), [fetchData]);
  const fetchLabSamples = useCallback((force = false) => fetchData('labSamples', getLabSamples, force), [fetchData]);
  const fetchReferralCases = useCallback((force = false) => fetchData('referralCases', getReferralCases, force), [fetchData]);
  const fetchReferringClinics = useCallback((force = false) => fetchData('referringClinics', getReferringClinics, force), [fetchData]);
  const fetchReviewRequests = useCallback((force = false) => fetchData('reviewRequests', getReviewRequests, force), [fetchData]);
  const fetchStaff = useCallback((force = false) => fetchData('staff', getStaff, force), [fetchData]);
  const fetchDashboardSummary = useCallback((force = false) => fetchData('dashboardSummary', getDashboardSummary, force), [fetchData]);

  // Invalidate specific cache
  const invalidateCache = useCallback((key) => {
    setCache(prev => ({
      ...prev,
      [key]: { ...prev[key], timestamp: 0 }
    }));
  }, []);

  // Invalidate all cache
  const invalidateAllCache = useCallback(() => {
    setCache(prev => {
      const newCache = {};
      Object.keys(prev).forEach(key => {
        newCache[key] = { ...prev[key], timestamp: 0 };
      });
      return newCache;
    });
  }, []);

  // Pre-fetch all data on mount
  useEffect(() => {
    Promise.all([
      fetchTasks(),
      fetchLabSamples(),
      fetchReferralCases(),
      fetchReferringClinics(),
      fetchReviewRequests(),
      fetchStaff(),
      fetchDashboardSummary()
    ]).catch(console.error);
  }, [fetchTasks, fetchLabSamples, fetchReferralCases, fetchReferringClinics, fetchReviewRequests, fetchStaff, fetchDashboardSummary]);

  const value = {
    // Data
    tasks: cache.tasks.data,
    labSamples: cache.labSamples.data,
    referralCases: cache.referralCases.data,
    referringClinics: cache.referringClinics.data,
    reviewRequests: cache.reviewRequests.data,
    staff: cache.staff.data,
    dashboardSummary: cache.dashboardSummary.data,
    
    // Loading states
    loading,
    
    // Fetch functions (for refresh)
    fetchTasks,
    fetchLabSamples,
    fetchReferralCases,
    fetchReferringClinics,
    fetchReviewRequests,
    fetchStaff,
    fetchDashboardSummary,
    
    // Cache management
    invalidateCache,
    invalidateAllCache
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
